import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { formatTimeAgo } from "@/lib/mock-data";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EditHistoryTimeline } from "@/components/edit-history-timeline";
import { UpgradeModal } from "@/components/upgrade-modal";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { GenerationDetailSkeleton } from "@/components/skeletons";
import { toast } from "sonner";
import {
  editImageAction,
  deleteGenerationAction,
  fetchGenerationChain,
  getServerCreditBalance,
  getServerSubscriptionDetails,
} from "@/lib/server-data";
import { Generation } from "@/types/generation";
import { CREDIT_ALLOWANCES } from "@/lib/stripe-config";

interface GenerationSearch {
  edit?: boolean;
}

export const Route = createFileRoute("/_authenticated/generation/$id")({
  // No loader - all data fetched client-side
  component: GenerationDetailPage,
  validateSearch: (search: Record<string, unknown>): GenerationSearch => {
    return {
      edit: search.edit === true || search.edit === "true",
    };
  },
});

function GenerationDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, region } = Route.useRouteContext();

  const [editPrompt, setEditPrompt] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch generation chain client-side
  const { data: chainData, isPending: chainLoading } = useQuery({
    queryKey: ["generation-chain", id],
    queryFn: () => fetchGenerationChain({ data: { id } }),
    staleTime: 2 * 60 * 1000,
  });

  // Fetch credits client-side
  const { data: balance, isPending: balanceLoading } = useQuery({
    queryKey: ["credits", session?.user?.id],
    queryFn: getServerCreditBalance,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch subscription client-side
  const { data: subscription, isPending: subLoading } = useQuery({
    queryKey: ["subscription", session?.user?.id],
    queryFn: () =>
      getServerSubscriptionDetails({
        data: { userId: session!.user.id },
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Edit image mutation with optimistic updates
  // MUST be declared before any conditional returns (Rules of Hooks)
  const editMutation = useMutation({
    mutationFn: async ({
      imageId,
      userInput,
    }: {
      imageId: string;
      userInput: string;
    }) => {
      return editImageAction({
        data: { imageId, userInput },
      });
    },
    onMutate: async () => {
      // Optimistically update credit count
      if (balance) {
        queryClient.setQueryData(["credits", session?.user?.id], {
          ...balance,
          credits: balance.credits - 1,
        });
      }
    },
    onSuccess: async (editedGeneration) => {
      // Refetch credits to get actual server state after editing
      queryClient.refetchQueries({
        queryKey: ["credits", session?.user?.id],
        type: "active",
      });

      // Invalidate other queries for when user navigates around
      queryClient.invalidateQueries({ queryKey: ["generation-chain", id] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-generations", session?.user?.id],
      });

      // Navigate to the new generation
      navigate({ to: `/generation/${editedGeneration.id}` });
    },
    onError: (error) => {
      // Rollback optimistic update by refetching server state
      queryClient.refetchQueries({
        queryKey: ["credits", session?.user?.id],
        type: "active",
      });

      console.error("Failed to edit image:", error);

      // Check if it's a 402 (insufficient credits) error
      if (error instanceof Error && error.message.includes("402")) {
        setShowUpgradeModal(true);
      } else {
        toast.error("Failed to edit image", {
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
  });

  const handleEdit = async () => {
    if (!editPrompt.trim() || !generation) return;

    // Check if user has credits before attempting edit
    if (!balance || !("credits" in balance) || balance.credits < 1) {
      setShowUpgradeModal(true);
      return;
    }

    setIsEditing(true);
    try {
      await editMutation.mutateAsync({
        imageId: generation.id,
        userInput: editPrompt,
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleDownload = () => {
    if (!generation) return;
    const link = document.createElement("a");
    link.href = generation.imageUrl;
    link.download = `generation-${generation.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // Call server action with JSON body
      const response = await deleteGenerationAction({
        data: {
          id,
        },
      });

      if (!response) {
        throw new Error("Failed to delete the image");
      }

      // Navigate to dashboard FIRST, then invalidate
      // This prevents trying to reload a deleted generation
      await navigate({ to: "/dashboard" });

      // Invalidate queries to refresh dashboard
      await queryClient.invalidateQueries({
        queryKey: ["dashboard-generations", session?.user?.id],
      });
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Failed to delete generation");
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Show skeleton during loading (after all hooks)
  if (chainLoading || balanceLoading || subLoading) {
    return <GenerationDetailSkeleton />;
  }

  // Get current generation from chain
  const generation =
    chainData &&
    "chain" in chainData &&
    chainData.chain &&
    chainData.currentIndex !== undefined
      ? chainData.chain[chainData.currentIndex]
      : null;

  // Not found state
  if (!generation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex min-h-[400px] flex-col items-center justify-center space-y-4">
          <svg
            className="h-16 w-16 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-2xl font-bold">Generation Not Found</h2>
          <p className="text-muted-foreground">
            The generation you're looking for doesn't exist.
          </p>
          <Link to="/dashboard">
            <Button className="rounded-full">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        creditsRemaining={balance && "credits" in balance ? balance.credits : 0}
        subscription={subscription}
        region={region}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="rounded-full">
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT COLUMN - Image */}
            <div className="space-y-4 lg:col-span-2">
              {/* Image */}
              <div className="overflow-hidden rounded-2xl border border-border bg-card">
                <img
                  src={generation.imageUrl}
                  alt={generation.prompt}
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-auto max-h-[60vh] lg:max-h-[80vh] object-contain bg-muted/20"
                />
              </div>

              {/* Mobile Actions - visible only on mobile */}
              <div className="space-y-4 lg:hidden">
                {/* Edit Interface - Mobile */}
                <div className="space-y-4 rounded-2xl border border-border bg-muted/50 p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Edit Image</h3>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-prompt-mobile">
                      What would you like to change?
                    </Label>
                    <Textarea
                      id="edit-prompt-mobile"
                      placeholder="e.g., Make the sky darker, add more contrast, change the color to blue..."
                      value={editPrompt}
                      onChange={(e) => setEditPrompt(e.target.value)}
                      disabled={isEditing}
                      className="min-h-32 rounded-2xl"
                    />
                    {balance && "credits" in balance && "tier" in balance && (
                      <div className="flex justify-end">
                        <span className="text-xs text-muted-foreground">
                          {balance.credits}/{CREDIT_ALLOWANCES[balance.tier]}{" "}
                          credits
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleEdit}
                    disabled={!editPrompt.trim() || isEditing}
                    className="w-full rounded-full"
                  >
                    {isEditing ? (
                      <>
                        <span className="animate-pulse">Editing...</span>
                      </>
                    ) : (
                      "Apply Edits"
                    )}
                  </Button>
                </div>
                <Button
                  variant="secondary"
                  className="w-full rounded-full"
                  onClick={handleDownload}
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download
                </Button>
                <Button
                  variant="destructive"
                  className="w-full rounded-full"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Delete
                </Button>
              </div>

              {/* Mobile Metadata - visible only on mobile */}
              <div className="space-y-6 lg:hidden">
                {/* Prompt */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Prompt
                  </h3>
                  <p className="text-base leading-relaxed">
                    {generation.prompt}
                  </p>
                </div>

                {/* Metadata */}
                <div className="flex flex-wrap gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Created
                    </p>
                    <p className="text-sm">
                      {formatTimeAgo(generation.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Actions & Metadata (Desktop Only) */}
            <div className="hidden lg:block lg:col-span-1 lg:sticky lg:top-8 lg:self-start">
              <div className="space-y-6">
                {/* Actions */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Actions
                  </h3>
                  {/* Edit Interface - Desktop */}
                  <div className="space-y-4 rounded-2xl border border-border bg-muted/50 p-4">
                    <h3 className="text-sm font-semibold">Edit Image</h3>
                    <div className="space-y-2">
                      <Label htmlFor="edit-prompt-desktop" className="text-xs">
                        What would you like to change?
                      </Label>
                      <Textarea
                        id="edit-prompt-desktop"
                        placeholder="e.g., Make the sky darker, add more contrast..."
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        disabled={isEditing}
                        className="min-h-24 text-sm rounded-2xl"
                      />
                      {balance && "credits" in balance && "tier" in balance && (
                        <div className="flex justify-end">
                          <span className="text-xs text-muted-foreground">
                            {balance.credits}/{CREDIT_ALLOWANCES[balance.tier]}{" "}
                            credits
                          </span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleEdit}
                      disabled={!editPrompt.trim() || isEditing}
                      className="w-full rounded-full"
                      size="sm"
                    >
                      {isEditing ? (
                        <span className="animate-pulse">Editing...</span>
                      ) : (
                        "Apply Edits"
                      )}
                    </Button>
                  </div>

                  <Button
                    variant="secondary"
                    className="w-full rounded-full"
                    onClick={handleDownload}
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Download
                  </Button>

                  <Button
                    variant="destructive"
                    className="w-full rounded-full"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </Button>
                </div>

                {/* Prompt */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Prompt
                  </h3>
                  <p className="text-sm leading-relaxed">{generation.prompt}</p>
                </div>

                {/* Metadata */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Created
                      </span>
                      <span className="text-sm">
                        {formatTimeAgo(generation.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Edit History Timeline */}
          {chainData && "chain" in chainData && chainData.chain && (
            <EditHistoryTimeline
              chain={chainData.chain.map((gen: Generation) => ({
                ...gen,
                createdAt: new Date(gen.createdAt),
              }))}
              currentIndex={chainData.currentIndex}
            />
          )}
        </div>
      </div>
    </div>
  );
}
